export function getPropertyRatePackageUpgradeTarget(rate: App.EnrichedPropertyRate): App.EnrichedPropertyRate | undefined {
  return rate.relations.upgradeTargets?.package
}

export function getPropertyRatePackageUpgradeSelectedKey(rate: App.EnrichedPropertyRate): string | undefined {
  if (!getPropertyRatePackageUpgradeTarget(rate)) return
  return `propertyRateId=${rate.rateId}`
}

/**
 * One refundability option of a package, as shown in the upgrade modal's radio group. Options
 * without a target still render, greyed out, so the customer can see what the upgrade costs them.
 */
export interface PropertyRateUpgradeVariant {
  rate: App.EnrichedPropertyRate
  /** The rate this variant upgrades to, if the service offers an upgrade on this variant. */
  target: App.EnrichedPropertyRate | undefined
  selectable: boolean
}

/** The upgrade a single package option offers, once its source refundability has been chosen. */
export interface PropertyRateUpgradeCandidate {
  sourceRate: App.EnrichedPropertyRate
  targetRate: App.EnrichedPropertyRate
  /** Every refundability option of the source package, in the group's own order. */
  variants: Array<PropertyRateUpgradeVariant>
}

/**
 * The LuxPlus+ flags that decide whether a member price is the one actually charged.
 */
export interface LuxPlusPricingContext {
  luxPlusEnabled: boolean
  canRedeemLuxPlusBenefits: boolean
  isGhaPricing: boolean
}

/**
 * The figure the customer will actually be charged for a rate, inclusive of taxes and fees.
 *
 * Gated on *redeeming* member benefits, not viewing them: free-preview and pending-renewal members
 * are shown member pricing by `useLuxPlusDisplayedMode` but are charged the regular price. The
 * upgrade surfaces quote what will be charged, so they deliberately differ from that hook here.
 */
export function getPropertyRatePayablePrice(
  rate: App.EnrichedPropertyRate,
  context: LuxPlusPricingContext,
  discountAmount: number = 0,
): number {
  const price = rate.totals.displayPrice
  const memberPrice = rate.luxPlusTotals?.displayPrice ?? 0
  const paysMemberPrice =
    context.luxPlusEnabled &&
    context.canRedeemLuxPlusBenefits &&
    !context.isGhaPricing &&
    memberPrice > 0 &&
    memberPrice < price

  return Math.max((paysMemberPrice ? memberPrice : price) - discountAmount, 0)
}

export function getPropertyRateUpgradeVariants(
  rates: Array<App.EnrichedPropertyRate>,
): Array<PropertyRateUpgradeVariant> {
  return rates.map(rate => {
    const target = getPropertyRatePackageUpgradeTarget(rate)
    return { rate, target, selectable: !!target }
  })
}

/**
 * The upgrade a package option advertises, or nothing if none of its refundability options has one.
 *
 * Rates arrive pre-sorted non-refundable first (see `useGroupedPropertyRatesByRefundability`), so the
 * first option carrying a target is also the cheapest — which is the one Apply lands on, even when
 * the customer is currently viewing a different refundability option.
 */
export function getPropertyRateUpgradeCandidate(
  rates: Array<App.EnrichedPropertyRate>,
): PropertyRateUpgradeCandidate | undefined {
  const variants = getPropertyRateUpgradeVariants(rates)
  const upgradeable = variants.find(variant => !!variant.target)
  if (!upgradeable?.target) return

  return {
    sourceRate: upgradeable.rate,
    targetRate: upgradeable.target,
    variants,
  }
}
